export type RefusalReason =
  | 'NOT_FOUND'
  | 'REVOKED'
  | 'EXPIRED'
  | 'CLOSED'
  | 'ALREADY_DECIDED'
  | 'PERMISSION_DENIED'
  | 'NETWORK'
  | 'GENERIC'

export type ApprovalErrorClassification =
  | RefusalReason
  | 'CAP'
  | 'INVALID_CODE'

export const REFUSAL_REASONS = new Set<RefusalReason>([
  'NOT_FOUND',
  'REVOKED',
  'EXPIRED',
  'CLOSED',
  'ALREADY_DECIDED',
  'PERMISSION_DENIED',
  'NETWORK',
  'GENERIC',
])

export function isRefusalReason(reason: string): reason is RefusalReason {
  return REFUSAL_REASONS.has(reason as RefusalReason)
}

export function classifyApprovalError(err: unknown): ApprovalErrorClassification {
  if (!err) return 'GENERIC'

  const errObj = err as {
    code?: string
    name?: string
    message?: string
    details?: { reason?: string }
  }

  const rawCode = String(errObj.code || '')
  // Normalize Firebase functions callable prefix e.g. 'functions/not-found' -> 'not-found'
  const code = rawCode.startsWith('functions/') ? rawCode.slice('functions/'.length) : rawCode
  const detailsReason = errObj.details?.reason

  // 1. Check offline or network errors
  if (
    (typeof navigator !== 'undefined' && navigator.onLine === false) ||
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    errObj.name === 'NetworkError' ||
    (err instanceof TypeError && /failed to fetch|networkerror/i.test(err.message))
  ) {
    return 'NETWORK'
  }

  // 2. Firebase Auth OTP codes
  if (
    code === 'auth/invalid-verification-code' ||
    code === 'auth/code-expired' ||
    code.includes('invalid-verification-code') ||
    code.includes('code-expired')
  ) {
    return 'INVALID_CODE'
  }

  // 3. Not found
  if (code === 'not-found') {
    return 'NOT_FOUND'
  }

  // 4. Failed precondition with link state reasons
  if (code === 'failed-precondition') {
    if (detailsReason === 'REVOKED') return 'REVOKED'
    if (detailsReason === 'EXPIRED') return 'EXPIRED'
    if (detailsReason === 'CLOSED') return 'CLOSED'
    if (detailsReason === 'ALREADY_DECIDED') return 'ALREADY_DECIDED'
    return 'GENERIC'
  }

  // 5. Permission denied
  if (code === 'permission-denied') {
    return 'PERMISSION_DENIED'
  }

  // 6. Resource exhausted (cap reached)
  if (code === 'resource-exhausted') {
    return 'CAP'
  }

  return 'GENERIC'
}
